import filedownload from "js-file-download";
import { AxiosResponse } from "axios";
import { FileEnums } from "models/enums";
import { EnumHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import mime from "mime";
import { FileConstants } from "utils/constants";

const FileHelper = {
  download: (response: AxiosResponse<void>) => {
    // axios types header values as AxiosHeaderValue | undefined; the API always sends a single content-type string.
    const contentType = response.headers["content-type"] as string;
    const filename =
      FileHelper.getFileNameFromContentDisposition(response.headers["content-disposition"]) ||
      FileHelper.getEmptyFileName(contentType);

    filedownload(response.data as unknown as string, filename);
  },

  getFileNameFromContentDisposition: (disposition?: string): string | undefined => {
    if (!disposition || disposition.indexOf("attachment") === -1) return undefined;

    const filenameRegex = /(?:.*filename\*|filename)=(?:([^'"]*)''|("))([^;]+)\2(?:[;`\n]|$)/;
    const matches = filenameRegex.exec(disposition);
    return matches !== null && matches[3] ? decodeURIComponent(matches[3]) : undefined;
  },

  getContentTypeList: () => {
    return EnumHelper.getItemValues(FileEnums.ContentTypes) as string[];
  },

  getFileExtension: (contentType: string) => {
    if (contentType === FileEnums.ContentTypes.ImageJPG) contentType = FileEnums.ContentTypes.ImageJPEG;

    return mime.getExtension(contentType);
  },

  getEmptyFileName: (contentType: string) => {
    return `${FileConstants.EmptyFileName}.${FileHelper.getFileExtension(contentType)}`;
  },

  getImageList: () => {
    return [
      FileEnums.ContentTypes.ImagePNG,
      FileEnums.ContentTypes.ImageXPNG,
      FileEnums.ContentTypes.ImageJPG,
      FileEnums.ContentTypes.ImageJPEG,
      FileEnums.ContentTypes.ImagePJPG,
      FileEnums.ContentTypes.ImageBMP,
      FileEnums.ContentTypes.ImageHEIC,
      FileEnums.ContentTypes.ImageHEIF,
    ];
  },

  getSharedDocumentTypes: () => [
    ...FileHelper.getImageList(),
    FileEnums.ContentTypes.ApplicationPDF,
    FileEnums.ContentTypes.SIE,
    FileEnums.ContentTypes.Zip,
    FileEnums.ContentTypes.XZip,
    FileEnums.ContentTypes.Docx,
    FileEnums.ContentTypes.Xlsx,
    FileEnums.ContentTypes.CSV,
    FileEnums.ContentTypes.Text,
  ],

  getImageMimeTypes: () => {
    const imageTypes = FileHelper.getImageList();

    return imageTypes.map((c) => c.toString());
  },

  getIconTextByContentType: (contentType?: string) => {
    if (contentType === undefined) return { iconText: FileConstants.IconTextCommon, iconColor: cssExports.iconCommon };

    const contentTypeList = FileHelper.getContentTypeList();
    contentType = contentType as keyof typeof FileEnums.ContentTypes;

    let iconText = FileConstants.IconTextCommon;
    let iconColor = cssExports.iconCommon;
    const contentTypeIndex = contentTypeList.indexOf(contentType);

    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    if (contentTypeIndex != undefined) {
      switch (contentType) {
        case FileEnums.ContentTypes.ApplicationPDF.toString():
          iconText = "PDF";
          iconColor = cssExports.iconPdf;
          break;
        case FileEnums.ContentTypes.Text.toString():
        case FileEnums.ContentTypes.SIE.toString():
          iconText = "TXT";
          iconColor = cssExports.iconText;
          break;
        case FileEnums.ContentTypes.TextXML.toString():
          iconText = "XML".toString();
          iconColor = cssExports.iconXml;
          break;
        case FileEnums.ContentTypes.Zip.toString():
        case FileEnums.ContentTypes.XZip.toString():
          iconText = "ZIP";
          iconColor = cssExports.iconZip;
          break;
        case FileEnums.ContentTypes.CSV.toString():
        case FileEnums.ContentTypes.MsCSV.toString():
          iconText = "CSV";
          iconColor = cssExports.iconCsv;
          break;
        case FileEnums.ContentTypes.Html.toString():
          iconColor = cssExports.iconHtml;
          iconText = "HTML";
          break;
        case FileEnums.ContentTypes.Doc.toString():
          iconColor = cssExports.iconDoc;
          iconText = "DOC";
          break;
        case FileEnums.ContentTypes.Docx.toString():
          iconColor = cssExports.iconDocx;
          iconText = "DOCX";
          break;
        case FileEnums.ContentTypes.Xlsx.toString():
          iconColor = cssExports.iconCsv;
          iconText = "XLSX";
          break;
        default:
          iconText = FileConstants.IconTextCommon;
          iconColor = cssExports.iconCommon;
          break;
      }
    }

    return { iconText, iconColor };
  },

  getDisplayTextByContentType: (contentType: string) => {
    const contentTypeList = FileHelper.getContentTypeList();
    contentType = contentType as keyof typeof FileEnums.ContentTypes;

    let displayType = FileConstants.IconTextCommon;
    const contentTypeIndex = contentTypeList.indexOf(contentType);

    // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
    if (contentTypeIndex != undefined) {
      switch (contentType) {
        case FileEnums.ContentTypes.ImagePNG.toString():
        case FileEnums.ContentTypes.ImageXPNG.toString():
          displayType = "png";
          break;
        case FileEnums.ContentTypes.ImageJPG.toString():
          displayType = "jpg";
          break;
        case FileEnums.ContentTypes.ImageJPEG.toString():
          displayType = "jpeg";
          break;
        case FileEnums.ContentTypes.ImagePJPG.toString():
          displayType = "pjpeg";
          break;
        case FileEnums.ContentTypes.ImageBMP.toString():
          displayType = "bmp";
          break;
        case FileEnums.ContentTypes.ImageHEIC.toString():
        case FileEnums.ContentTypes.ImageHEIF.toString():
          displayType = "heic";
          break;
        case FileEnums.ContentTypes.ApplicationPDF.toString():
          displayType = "pdf";
          break;
        case FileEnums.ContentTypes.Text.toString():
          displayType = "txt";
          break;
        case FileEnums.ContentTypes.SIE.toString():
          displayType = "SIE";
          break;
        case FileEnums.ContentTypes.TextXML.toString():
          displayType = "xml".toString();
          break;
        case FileEnums.ContentTypes.Zip.toString():
        case FileEnums.ContentTypes.XZip.toString():
          displayType = "zip";
          break;
        case FileEnums.ContentTypes.CSV.toString():
          displayType = "csv";
          break;
        case FileEnums.ContentTypes.MsCSV.toString():
          displayType = "xls";
          break;
        case FileEnums.ContentTypes.Xlsx.toString():
          displayType = "xlsx";
          break;
        case FileEnums.ContentTypes.Html.toString():
          displayType = "html";
          break;
        case FileEnums.ContentTypes.ApplicationJson.toString():
          displayType = "json";
          break;
        case FileEnums.ContentTypes.ApplicationEncryptedPgp.toString():
          displayType = "pgp";
          break;
        case FileEnums.ContentTypes.ApplicationPgpSignature.toString():
          displayType = "asc";
          break;
        case FileEnums.ContentTypes.ApplicationPgpKeys.toString():
          displayType = "pgp";
          break;
        case FileEnums.ContentTypes.Doc.toString():
          displayType = "doc";
          break;
        case FileEnums.ContentTypes.Docx.toString():
          displayType = "docx";
          break;
        case FileEnums.ContentTypes.ApplicationOctetStream.toString():
        default:
          displayType = "";
          break;
      }
    }

    return displayType;
  },

  isImage: (contentType: string) => {
    const contentTypeList = FileHelper.getContentTypeList();
    contentType = contentType as keyof typeof FileEnums.ContentTypes;

    let isImage = false;
    const contentTypeIndex = contentTypeList.indexOf(contentType);

    if (contentTypeIndex !== undefined && contentTypeIndex >= 0) {
      const imageList = FileHelper.getImageMimeTypes();
      const imageIndex = imageList.indexOf(contentType);

      if (imageIndex !== undefined && imageIndex >= 0) {
        isImage = true;
      }
    }

    return isImage;
  },

  bytesToSize: (bytes: number) => {
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
  },

  readByteArrayAsString: (fileReaderResult: number[]) => {
    let binary = "";

    if (fileReaderResult == null) {
      return binary;
    } else {
      const fileDataAsBytes = new Uint8Array(fileReaderResult);

      for (let i = 0; i < fileDataAsBytes.length; i++) {
        binary += String.fromCharCode(fileDataAsBytes[i]);
      }

      return window.btoa(binary);
    }
  },

  readFileAsBase64String: (fileReaderResult: ArrayBuffer | null) => {
    let binary = "";

    if (fileReaderResult === null) {
      return binary;
    } else {
      const fileDataAsBytes = new Uint8Array(fileReaderResult);

      for (let i = 0; i < fileDataAsBytes.length; i++) {
        binary += String.fromCharCode(fileDataAsBytes[i]);
      }

      return window.btoa(binary);
    }
  },

  /**
   * Gets content type from file extension using mime library.
   * Handles special cases for file types that may not be in standard mime database.
   * @param fileName - File name to extract extension from
   * @returns Content type string or undefined if extension not found
   */
  getContentTypeFromFileName: (fileName?: string): string | undefined => {
    if (!fileName) {
      return undefined;
    }

    // Handle special cases that may not be in standard mime database
    const extension = fileName.split(".").pop()?.toLowerCase();
    if (!extension) {
      return undefined;
    }

    const specialCases: Record<string, string> = {
      heic: FileEnums.ContentTypes.ImageHEIC,
      heif: FileEnums.ContentTypes.ImageHEIF,
    };

    if (specialCases[extension]) {
      return specialCases[extension];
    }

    // Use mime library for standard file types
    // mime v3: getType() accepts filename or extension
    try {
      const mimeType = mime.getType(fileName);
      return mimeType || undefined;
    } catch {
      return undefined;
    }
  },

  /**
   * Detects content type from base64 data by checking magic bytes/file signatures.
   * @param base64Data - Base64 encoded file data
   * @returns Content type string or undefined if detection fails
   */
  detectContentTypeFromData: (base64Data?: string): string | undefined => {
    if (!base64Data || base64Data.length === 0) {
      return undefined;
    }

    try {
      // Decode first few bytes to check magic bytes
      const binaryString = window.atob(base64Data.substring(0, Math.min(100, base64Data.length)));
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // HEIC/HEIF: ftyp box at offset 4, check for "heic", "heif", "mif1"
      if (bytes.length >= 12) {
        const ftyp = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]);
        if (ftyp === "ftyp") {
          const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
          if (brand === "heic" || brand === "mif1") {
            return FileEnums.ContentTypes.ImageHEIC;
          }
          if (brand === "heif") {
            return FileEnums.ContentTypes.ImageHEIF;
          }
        }
      }

      return undefined;
    } catch {
      return undefined;
    }
  },

  /**
   * Normalizes content type by falling back to file extension detection when contentType is empty or missing.
   * This handles cases where browsers may not detect MIME types correctly (e.g., Windows browsers with HEIC files).
   * @param fileName - File name to check for extension fallback
   * @param existingContentType - Existing content type (may be empty or incorrect)
   * @returns Normalized content type, or existing one if valid, or empty string if neither available
   */
  normalizeContentType: (fileName?: string, existingContentType?: string, fileData?: string): string => {
    // If we already have a valid content type, use it
    if (
      existingContentType &&
      existingContentType.trim() !== "" &&
      existingContentType !== FileEnums.ContentTypes.ApplicationOctetStream
    ) {
      return existingContentType;
    }

    // Fallback to file extension detection
    const contentTypeFromExtension = FileHelper.getContentTypeFromFileName(fileName);
    if (contentTypeFromExtension) {
      return contentTypeFromExtension;
    }

    if (fileData) {
      const contentTypeFromData = FileHelper.detectContentTypeFromData(fileData);
      if (contentTypeFromData) {
        return contentTypeFromData;
      }
    }

    // Return existing content type if available, otherwise empty string
    return existingContentType ?? "";
  },

  allowUpload: (fileName?: string, contentType?: string, allowedTypes?: FileEnums.ContentTypes[]) => {
    if (!contentType || contentType.trim() === "" || contentType === FileEnums.ContentTypes.ApplicationOctetStream) {
      contentType = FileHelper.normalizeContentType(fileName, contentType);
    }

    // Windows reports .csv with the legacy Excel type, so a list that allows CSV but not .xls would refuse it.
    // During dragenter the name is withheld; the drop re-validates with it.
    if (contentType === FileEnums.ContentTypes.MsCSV && (!fileName || fileName.toLowerCase().endsWith(".csv"))) {
      contentType = FileEnums.ContentTypes.CSV;
    }

    // SIE's content type is empty, which any unrecognised file also ends up with, so SIE is matched on the extension too.
    // During dragenter the browser withholds the file name; the SIE match passes then and the drop re-validates with the name.
    const matchesAllowedType = (type: FileEnums.ContentTypes) =>
      // eslint-disable-next-line eqeqeq --- comparing different types
      type == contentType && (type !== FileEnums.ContentTypes.SIE || !fileName || FileHelper.hasSieExtension(fileName));

    if (allowedTypes !== undefined && allowedTypes.length > 0 && allowedTypes.some(matchesAllowedType)) {
      return true;
    }

    return false;
  },

  hasSieExtension: (fileName?: string) => {
    const lowerCaseFileName = fileName?.toLowerCase() ?? "";
    return FileConstants.SieFileExtensions.some((extension) => lowerCaseFileName.endsWith(extension));
  },

  /** The type options a FileUploader field hands its container, including the defaults for options the field leaves unset. */
  getFileUploaderTypeOptions: (options: {
    allowAnyContentType?: boolean;
    allowAnyImageTypes?: boolean;
    allowedTypes?: FileEnums.ContentTypes[];
  }) => ({
    allowAnyContentType: options.allowAnyContentType ?? false,
    allowAnyImageType: options.allowAnyImageTypes || !options.allowedTypes?.length,
    allowedTypes: options.allowedTypes ?? [FileEnums.ContentTypes.ApplicationPDF],
  }),

  getAcceptedFileTypes: (
    allowAnyContentType: boolean,
    allowAnyImageType?: boolean,
    allowedTypes?: FileEnums.ContentTypes[]
  ) => {
    let acceptedFileTypes: FileEnums.ContentTypes[] = [];

    if (allowAnyContentType) {
      acceptedFileTypes = EnumHelper.getItemValues(FileEnums.ContentTypes) as FileEnums.ContentTypes[];
    } else {
      if (allowAnyImageType) {
        acceptedFileTypes = acceptedFileTypes.concat(FileHelper.getImageList());
      }

      if (allowedTypes !== undefined && allowedTypes.length > 0) {
        acceptedFileTypes = acceptedFileTypes.concat(allowedTypes);
      }
    }

    return acceptedFileTypes;
  },

  generateAcceptedString: (allowedTypes: FileEnums.ContentTypes[]) => {
    let acceptedFileTypes = "";

    if (allowedTypes !== undefined && allowedTypes.length > 0) {
      // SIE has no MIME type, so the file picker can only filter it by extension.
      const acceptTokens = allowedTypes.map((type) =>
        type === FileEnums.ContentTypes.SIE ? FileConstants.SieFileExtensions.join(", ") : type
      );
      acceptedFileTypes = acceptedFileTypes.concat(acceptTokens.join(", "));
    }

    return acceptedFileTypes;
  },

  generateAllowedTypesMessage: (allowedContentTypes: FileEnums.ContentTypes[]) => {
    let allowedTypes = "";

    if (allowedContentTypes !== undefined && allowedContentTypes.length > 0) {
      const allowedTypeNames = allowedContentTypes.map((type) => FileHelper.getDisplayTextByContentType(type));

      const withoutDuplicates = Array.from(new Set(allowedTypeNames));

      allowedTypes = withoutDuplicates.toString().replace(/,/g, ", ");
    }

    return allowedTypes;
  },

  /**
   * Shortens a file name to maxLength by cutting the middle, keeping the start and end (extension)
   * visible -- the two parts that actually distinguish one upload from another in a list.
   */
  truncateMiddle: (fileName: string, maxLength = 28) => {
    if (fileName.length <= maxLength) return fileName;

    const keep = maxLength - 1; // 1 char reserved for the ellipsis
    const headLength = Math.ceil(keep / 2);
    const tailLength = Math.floor(keep / 2);

    return `${fileName.slice(0, headLength)}…${fileName.slice(fileName.length - tailLength)}`;
  },

  getUTF8Encoding: (str: string) => {
    // Decode the Latin1 string into a byte array
    const byteArray = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      byteArray[i] = str.charCodeAt(i);
    }

    // Encode the byte array into a UTF-8 string
    const utf8String = new TextDecoder("utf-8").decode(byteArray);
    return utf8String;
  },
};

export default FileHelper;
